import {
  apiLogin,
  apiLogout,
  apiGetUsers,
  apiAddUser,
  apiUpdateUser,
  apiDeleteUser,
  setToken,
  clearToken,
  getToken,
  type ApiUser,
} from '@/utils/apiClient';

export type UserRole = 'admin' | 'editor' | 'support';

export interface AdminUser {
  id: string;
  username: string;
  displayName: string;
  password: string;
  role: UserRole;
  createdAt: string;
  lastLogin?: string;
}

export interface AuthSession {
  userId: string;
  username: string;
  displayName: string;
  role: UserRole;
}

const SESSION_KEY = 'ygAdminSession';

export const userStore = {
  // ── Session ────────────────────────────────────────────────────────────────

  getSession(): AuthSession | null {
    try {
      const raw = localStorage.getItem(SESSION_KEY);
      if (!raw) return null;
      const session = JSON.parse(raw) as AuthSession;
      // Token da yoksa session geçersiz say
      if (!getToken()) {
        this.clearSession();
        return null;
      }
      return session;
    } catch {
      return null;
    }
  },

  setSession(user: ApiUser, token: string): void {
    setToken(token);
    const session: AuthSession = {
      userId: user.id,
      username: user.username,
      displayName: user.displayName,
      role: user.role,
    };
    localStorage.setItem(SESSION_KEY, JSON.stringify(session));
  },

  clearSession(): void {
    clearToken();
    localStorage.removeItem(SESSION_KEY);
    localStorage.removeItem('ygAdminAuth');
  },

  isAuthenticated(): boolean {
    return this.getSession() !== null;
  },

  // ── Auth (API) ─────────────────────────────────────────────────────────────

  async login(username: string, password: string): Promise<AuthSession> {
    const result = await apiLogin(username, password);
    this.setSession(result.user, result.token);
    return {
      userId: result.user.id,
      username: result.user.username,
      displayName: result.user.displayName,
      role: result.user.role,
    };
  },

  async logout(): Promise<void> {
    try {
      await apiLogout();
    } catch {
      // Sessizce devam et
    }
    this.clearSession();
  },

  // ── Users (API) ────────────────────────────────────────────────────────────

  async getAll(): Promise<AdminUser[]> {
    const users = await apiGetUsers();
    return users.map((u) => ({
      id: u.id,
      username: u.username,
      displayName: u.displayName,
      password: '',
      role: u.role,
      createdAt: u.createdAt,
      lastLogin: u.lastLogin,
    }));
  },

  async addUser(data: Omit<AdminUser, 'id' | 'createdAt'>): Promise<AdminUser> {
    const result = await apiAddUser({
      username: data.username,
      displayName: data.displayName,
      password: data.password,
      role: data.role,
    });
    return {
      ...data,
      id: result.id,
      createdAt: new Date().toISOString(),
    };
  },

  async updateUser(id: string, updates: Partial<Omit<AdminUser, 'id' | 'createdAt'>>): Promise<boolean> {
    await apiUpdateUser(id, {
      username: updates.username,
      displayName: updates.displayName,
      password: updates.password,
      role: updates.role,
    });
    return true;
  },

  async deleteUser(id: string): Promise<boolean> {
    await apiDeleteUser(id);
    return true;
  },

  async usernameExists(username: string, excludeId?: string): Promise<boolean> {
    const users = await this.getAll();
    return users.some(
      (u) => u.username.toLowerCase() === username.toLowerCase() && u.id !== excludeId
    );
  },

  recordLogin(_id: string): void {
    // API login endpoint'i zaten last_login'i günceller
  },

  // Geriye dönük uyumluluk için senkron versiyonlar (artık kullanılmıyor ama import hatası olmasın)
  findByCredentials(_username: string, _password: string): null {
    return null;
  },
};
